import { ReactElement, useState } from 'react'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined'
import { alpha, Box, Button, Stack, Typography } from '@mui/material'
import useEnableNotifications, {
  getNotificationPermission,
} from '@editor/hooks/useEnableNotifications'
import { useAppStore } from '@stores'

/**
 * In a new, empty chat: a nudge to turn on notifications, so the user learns
 * when the assistant answered or waits for a decision while working elsewhere
 * (`useResponseNotifications`). Hidden once they are on; when the browser
 * blocks them, it says where to allow them instead, as the page cannot ask
 * again.
 */
const NotificationReminder = (): ReactElement | null => {
  const showNotifications = useAppStore(state => state.showNotifications)
  const { enableNotifications } = useEnableNotifications()
  // The browser does not announce permission changes, so the answer to our
  // own request is kept here.
  const [permission, setPermission] = useState(getNotificationPermission)

  if (permission === 'unsupported') return null
  if (showNotifications && permission === 'granted') return null

  const blocked = permission === 'denied'
  const Icon = blocked ? NotificationsOffOutlinedIcon : NotificationsActiveOutlinedIcon

  return (
    <Stack
      alignItems="center"
      spacing={1}
      data-cy="ai-chat-notification-reminder"
      sx={{
        maxWidth: 300,
        px: 2.5,
        py: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: '50%',
          color: blocked ? 'text.secondary' : 'primary.main',
          bgcolor: theme =>
            blocked ? theme.palette.action.hover : alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Typography variant="subtitle2">
        {blocked ? 'Benachrichtigungen blockiert' : 'Nichts verpassen'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {blocked
          ? 'Erlauben Sie Benachrichtigungen in den Website-Einstellungen Ihres Browsers, um zu erfahren, wann der Assistent geantwortet hat.'
          : 'Lassen Sie sich benachrichtigen, wenn der Assistent geantwortet hat oder auf Ihre Entscheidung wartet – auch in einem anderen Tab.'}
      </Typography>
      {!blocked && (
        <Button
          size="small"
          variant="contained"
          disableElevation
          onClick={async () => setPermission(await enableNotifications())}
          data-cy="ai-chat-enable-notifications"
          sx={{ mt: 0.5 }}
        >
          Benachrichtigungen erlauben
        </Button>
      )}
    </Stack>
  )
}

export default NotificationReminder
